import type {
  AppRouteDefinition,
  PageRouteConfig,
  RouteBuildContext,
  RouteHeaderDataState
} from './types';

import {registerFromModulePath} from './register';

type PageRouteFactory = () => PageRouteConfig;

type PageModule = Record<string, PageRouteFactory | unknown>;

const pageModules = import.meta.glob<PageModule>(
  ['../../pages/page.tsx', '../../pages/**/page.tsx'],
  {
    eager: true
  }
);

function getRouteFactory(mod: PageModule, modulePath: string): PageRouteFactory {
  const factoryKey = Object.keys(mod).find(
    (key) => key.startsWith('create') && key.endsWith('Route') && typeof mod[key] === 'function'
  );

  if (!factoryKey) {
    throw new Error(`No create*Route factory found in ${modulePath}`);
  }

  return mod[factoryKey] as PageRouteFactory;
}

function compareRoutes(a: AppRouteDefinition, b: AppRouteDefinition): number {
  const aSegments = a.path.split('/').filter(Boolean).length;
  const bSegments = b.path.split('/').filter(Boolean).length;

  if (bSegments !== aSegments) {
    return bSegments - aSegments;
  }

  return a.path.localeCompare(b.path);
}

function buildRouteRegistry() {
  const mergedInitialHeaderData: RouteHeaderDataState = {};
  const allRoutes: AppRouteDefinition[] = [];

  for (const [modulePath, mod] of Object.entries(pageModules)) {
    const factory = getRouteFactory(mod, modulePath);
    const config = factory();

    if (config.initialHeaderData) {
      Object.assign(mergedInitialHeaderData, config.initialHeaderData);
    }

    allRoutes.push(registerFromModulePath(modulePath, config));
  }

  allRoutes.sort(compareRoutes);

  return {
    initialHeaderData: mergedInitialHeaderData,
    publicRoutes: allRoutes.filter((route) => route.isProtected === false),
    protectedRoutes: allRoutes.filter((route) => route.isProtected !== false)
  };
}

/** Built once when the module is first imported (build / app boot). */
export const {initialHeaderData, publicRoutes, protectedRoutes} = buildRouteRegistry();

export function resolveLayout(layout: AppRouteDefinition['layout'], ctx: RouteBuildContext) {
  const {headerDataKey, resolveLink, ...rest} = layout;

  return {
    ...rest,
    headerData: headerDataKey ? ctx.headerData[headerDataKey] : undefined,
    link: resolveLink ? resolveLink(ctx) : layout.link
  };
}
